/**
 * Contact Detail Page
 * Display comprehensive contact information with tabs for interactions, vouchers, and membership history
 */

import React, { useEffect, useState } from "react";
import {
  useParams,
  useNavigate,
  useLocation,
  useSearchParams,
  Link,
} from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getContact,
  getContactMembershipHistory,
  getContactClassCompletions,
  getContactSubscriptions,
  getHouseholdMembers,
  getContactVouchers,
} from "../services/contacts.service";
import { getInteractions } from "../services/interactions.service";
import { getOrganization } from "../services/organizations.service";
import {
  getContactSegments,
  getStaticSegments,
  addContactsToSegment,
  removeContactsFromSegment,
} from "../services/segments.service";
import {
  getTasksForContact,
  getSupportAgentById,
} from "../services/supportTasksDb.service";
import { QUERY_KEYS } from "../utils/constants";
import { formatDate } from "../utils/formatters";
import {
  TASK_TYPE_LABELS,
  TASK_STATUS_CONFIG,
} from "../types/supportTasks.types";
import type {
  SupportTask,
  CustomerSnapshot,
} from "../types/supportTasks.types";
import Button from "../components/common/Button";
import LoadingSpinner from "../components/common/LoadingSpinner";
import Modal from "../components/common/Modal";
import {
  getSmsConsentStatus,
  getSmsSingleStatus,
} from "../services/sms.service";
import SmsConsentEditor from "../components/contacts/SmsConsentEditor";
import ContactSmsStatus from "../components/contacts/ContactSmsStatus";
import ContactForm from "../components/contacts/ContactForm";
import { refreshContactAfterEdit } from "./contactDetailRefresh";
import ContactTagsSection from "../components/contacts/ContactTagsSection";
import ContactOptOutBadges from "../components/contacts/ContactOptOutBadges";
import ContactPersonalSection from "../components/contacts/ContactPersonalSection";
import ContactAccountSection from "../components/contacts/ContactAccountSection";
import ContactCustomAttributesSection from "../components/contacts/ContactCustomAttributesSection";
import ContactNotesTab from "../components/contacts/ContactNotesTab";
import QuickActionsSection from "../components/contacts/QuickActionsSection";
import SendSMSModal from "../components/contacts/SendSMSModal";
import SendEmailModal from "../components/contacts/SendEmailModal";
import StartSequenceModal from "../components/contacts/StartSequenceModal";
import { isFieldManagerRole } from "../utils/fieldManagerAccess";
import ActivityLogTab from "../components/contacts/ActivityLogTab";
import EmailHistoryTab from "../components/contacts/EmailHistoryTab";
import EventCheckinsTab from "../components/contacts/EventCheckinsTab";
import { useSmsConversationsAvailable } from "../hooks/useSmsConversationsAvailable";
import IssueRewardModal from "../components/vouchers/IssueRewardModal";
import { useToast } from "../components/ui/Toast";
import { useAuth } from "../contexts/AuthContext";
import type { ClassCompletion } from "../types/contact.types";
import type { Segment } from "../types/segment.types";

/**
 * Badge colours keyed by voucherStatusLabel()'s vocabulary — the SAME labels the
 * Redemptions page and its CSV export use. Anything unrecognised falls back to the
 * neutral "active" blue rather than rendering an unstyled chip.
 */
const VOUCHER_STATUS_CLASSES: Record<string, string> = {
  Redeemed: "bg-green-100 text-green-800",
  Cancelled: "bg-red-100 text-red-800",
  Expired: "bg-gray-100 text-gray-600",
  Void: "bg-gray-100 text-gray-600",
  Pending: "bg-yellow-100 text-yellow-800",
  Test: "bg-purple-100 text-purple-800",
  Active: "bg-blue-100 text-blue-800",
};

const ContactDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { user } = useAuth();
  // Regional / Territory Managers (owner 2026-10-29): Interactions, Support Tasks and Segments are not their modules.
  const fieldManager = isFieldManagerRole(user?.role);

  // Get fromTaskId from location state (passed when navigating from Support Tasks)
  const fromTaskId = (location.state as { fromTaskId?: string })?.fromTaskId;
  const [activeTab, setActiveTab] = useState<
    | "overview"
    | "interactions"
    | "tasks"
    | "notes"
    | "activity"
    | "emails"
    | "vouchers"
    | "membership"
    | "segments"
    | "education"
    | "events"
    | "sms"
  >("overview");
  // Two-way SMS "SMS messages" tab: only when the server probe answers true.
  const { available: smsConversationsAvailable } =
    useSmsConversationsAvailable();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isEditModalOpen, setIsEditModalOpen] = useState(
    searchParams.get("edit") === "true",
  );
  const [showAddToSegmentDropdown, setShowAddToSegmentDropdown] =
    useState(false);
  const [isSMSModalOpen, setIsSMSModalOpen] = useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [isStartSequenceOpen, setIsStartSequenceOpen] = useState(false);
  const [isIssueRewardOpen, setIsIssueRewardOpen] = useState(false);

  // Check if user has access to Quick Actions (admin, superadmin, support roles).
  // Compare against the exact stored role values — do NOT strip underscores, or
  // support_admin/support_agent silently fail the match (they become supportadmin/supportagent).
  const canAccessQuickActions =
    user?.role &&
    ["superadmin", "admin", "support_admin", "support_agent"].includes(
      user.role.toLowerCase(),
    );

  // Fetch contact data
  const {
    data: contact,
    isLoading,
    error,
  } = useQuery({
    queryKey: [QUERY_KEYS.CONTACTS, id],
    queryFn: () => getContact(id!),
    enabled: !!id,
  });

  const smsConsent = useQuery({
    queryKey: ["sms-consent-status", contact?.id],
    queryFn: () => getSmsConsentStatus(contact!.id),
    enabled: !!contact?.id,
    staleTime: 0,
  });
  // The single SMS status (migration 998): what the Communication section and the header badge show.
  const smsSingle = useQuery({
    queryKey: ["sms-single-status", contact?.id],
    queryFn: () => getSmsSingleStatus(contact!.id),
    enabled: !!contact?.id,
    staleTime: 0,
  });

  // Assigned support agent ("Support Concierge") for the header link.
  // Reads assigned_support_agent_id — the authoritative column — NOT the
  // vestigial assigned_agent_id, which is null on every row.
  const assignedAgentId = contact?.assigned_support_agent_id;
  const { data: assignedAgent } = useQuery({
    queryKey: ["support-agent", assignedAgentId],
    queryFn: () => getSupportAgentById(assignedAgentId as UUID),
    enabled: !!assignedAgentId,
  });

  // Fetch organization data
  const { data: organization } = useQuery({
    queryKey: [QUERY_KEYS.ORGANIZATIONS, contact?.organization_id],
    queryFn: () => getOrganization(contact!.organization_id!),
    enabled: !!contact?.organization_id,
  });

  // Fetch subscriptions (always eager — displayed in Overview)
  const { data: subscriptions = [] } = useQuery({
    queryKey: ["contact-subscriptions", id],
    queryFn: () => getContactSubscriptions(id!),
    enabled: !!contact?.id,
  });

  // Fetch household members (only when household_key exists)
  const { data: householdMembers = [] } = useQuery({
    queryKey: ["household-members", contact?.household_key],
    queryFn: () => getHouseholdMembers(contact!.household_key!, id!),
    enabled: !!contact?.household_key,
  });

  // Fetch vouchers for Vouchers tab.
  // Identity is contact_id-first (email is only a fallback for vouchers that carry no
  // contact_id), so the query key must include BOTH — a contact whose email changes must
  // not read a cached list keyed on the old address.
  const { data: contactVouchersResult, isLoading: isLoadingVouchers } =
    useQuery({
      queryKey: ["contact-vouchers", id, contact?.email],
      queryFn: () =>
        getContactVouchers({ id: contact?.id, email: contact?.email }),
      enabled: !!(contact?.id || contact?.email) && activeTab === "vouchers",
    });
  const contactVouchers = contactVouchersResult?.rows ?? [];

  // Fetch interactions
  // contact_id is a FILTER (2nd argument). It used to be passed inside the pagination argument, where
  // it was ignored, and the result was read from a non-existent `.interactions` field (gap list M7).
  const { data: interactionsData } = useQuery({
    queryKey: [QUERY_KEYS.INTERACTIONS, "contact", id],
    queryFn: () =>
      getInteractions(
        { page: 1, limit: 100, sortBy: "interaction_date", sortOrder: "desc" },
        { contact_id: id },
      ),
    enabled: !!id && activeTab === "interactions",
  });

  // Fetch membership history
  const { data: membershipHistory } = useQuery({
    queryKey: ["membershipHistory", id],
    queryFn: () => getContactMembershipHistory(id!),
    enabled: !!id && activeTab === "membership",
  });

  // Fetch support tasks for contact
  const { data: contactTasks = [], isLoading: isLoadingTasks } = useQuery({
    queryKey: ["support-tasks", "contact", id],
    queryFn: () => getTasksForContact(id!),
    enabled: !!id && activeTab === "tasks",
  });

  // Fetch contact's segments
  const { data: contactSegments = [], isLoading: isLoadingSegments } = useQuery(
    {
      queryKey: [QUERY_KEYS.SEGMENTS, "contact", id],
      queryFn: () => getContactSegments(id!),
      enabled: !!id && activeTab === "segments",
    },
  );

  // Fetch class completions (Education tab)
  const { data: classCompletions = [], isLoading: isLoadingCompletions } =
    useQuery({
      queryKey: ["class-completions", id],
      queryFn: () => getContactClassCompletions(id!),
      enabled: !!id && activeTab === "education",
    });

  // Fetch all static segments (for "Add to Segment" dropdown)
  const { data: allStaticSegments = [] } = useQuery({
    queryKey: [QUERY_KEYS.SEGMENTS, "static"],
    queryFn: getStaticSegments,
    enabled: !!id && activeTab === "segments" && showAddToSegmentDropdown,
  });

  // Add to segment mutation
  const addToSegmentMutation = useMutation({
    mutationFn: async (segmentId: string) => {
      await addContactsToSegment(segmentId, [id!], user?.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.SEGMENTS, "contact", id],
      });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.SEGMENTS] });
      showToast("success", "Contact added to segment");
      setShowAddToSegmentDropdown(false);
    },
    onError: () => {
      showToast("error", "Failed to add contact to segment");
    },
  });

  // Remove from segment mutation
  const removeFromSegmentMutation = useMutation({
    mutationFn: async (segmentId: string) => {
      await removeContactsFromSegment(segmentId, [id!]);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.SEGMENTS, "contact", id],
      });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.SEGMENTS] });
      showToast("success", "Contact removed from segment");
    },
    onError: () => {
      showToast("error", "Failed to remove contact from segment");
    },
  });

  // Segments available to add (exclude ones contact is already in)
  const availableSegments = allStaticSegments.filter(
    (segment) => !contactSegments.some((cs) => cs.id === segment.id),
  );

  // Two-way SMS quick path: "Reply as <contact>" in an SMS thread links here with ?sendSms=1 and opens the
  // existing Send SMS modal (same consent and suppression gates). The parameter is consumed so a reload or
  // back-navigation does not reopen it.
  const sendSmsRequested = searchParams.get("sendSms") === "1";
  const contactPhoneForSms = contact?.phone_number || contact?.phone;
  useEffect(() => {
    if (!sendSmsRequested || !contact) {
      return;
    }
    if (canAccessQuickActions && contactPhoneForSms) {
      setIsSMSModalOpen(true);
    }
    const next = new URLSearchParams(searchParams);
    next.delete("sendSms");
    setSearchParams(next, { replace: true });
  }, [
    sendSmsRequested,
    contact,
    canAccessQuickActions,
    contactPhoneForSms,
    searchParams,
    setSearchParams,
  ]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner size="lg" text="Loading contact..." />
      </div>
    );
  }

  if (error || !contact) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">Error loading contact. Please try again.</p>
        <Button
          variant="secondary"
          onClick={() => navigate("/contacts")}
          className="mt-4"
        >
          Back to Contacts
        </Button>
      </div>
    );
  }

  const fullName = `${contact.first_name} ${contact.last_name}`;
  const displayPhone = contact.phone_number || contact.phone;
  const displayNotes = contact.description || contact.notes;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-4 mb-2">
            <button
              onClick={() => navigate("/contacts")}
              className="text-primary-600 hover:text-primary-800 flex items-center"
            >
              ← Back to Contacts
            </button>
            {fromTaskId && (
              <>
                <span className="text-gray-300">|</span>
                <Link
                  to="/support-tasks"
                  state={{ returnToTaskId: fromTaskId }}
                  className="text-blue-600 hover:text-blue-800 flex items-center gap-1 font-medium"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                    />
                  </svg>
                  Back to Task
                </Link>
              </>
            )}
          </div>
          <h1 className="text-3xl font-bold text-gray-900">{fullName}</h1>
          <p className="mt-1 text-gray-600">{contact.title || "Contact"}</p>
          {/* Assigned Support Concierge — links to that agent's contact list.
              Rendered only when the contact actually has an agent AND that agent
              still resolves, so a dangling reference shows nothing rather than a
              link to an agent who no longer exists. */}
          {assignedAgent && (
            <p className="mt-1 text-sm text-gray-600">
              Assigned Support Concierge:{" "}
              <Link
                to={`/contacts?agent=${assignedAgent.id}&from=agent`}
                className="text-primary-600 hover:text-primary-700 hover:underline font-medium"
              >
                {assignedAgent.name}
              </Link>
              {!assignedAgent.is_active && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                  Inactive
                </span>
              )}
            </p>
          )}
          {/* Opt-out / suppression visibility for support & marketing */}
          <ContactOptOutBadges
            contact={contact}
            smsConsentState={smsConsent.data ?? null}
            smsStatus={smsSingle.data?.sms_status ?? null}
            className="mt-2"
          />
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="secondary"
            onClick={() => setIsIssueRewardOpen(true)}
          >
            Issue Reward
          </Button>
          <Button variant="primary" onClick={() => setIsEditModalOpen(true)}>
            Edit Contact
          </Button>
        </div>
      </div>

      {/* Tags Section - at top of record */}
      <ContactTagsSection contactId={contact.id} editable={true} />

      {/* Quick Actions - for admin, superadmin, and support roles */}
      {canAccessQuickActions && (
        <QuickActionsSection
          contact={contact}
          onSendSMS={displayPhone ? () => setIsSMSModalOpen(true) : undefined}
          onSendEmail={
            contact.email ? () => setIsEmailModalOpen(true) : undefined
          }
          onStartSequence={() => setIsStartSequenceOpen(true)}
        />
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8 overflow-x-auto">
          <button
            onClick={() => setActiveTab("overview")}
            className={`${
              activeTab === "overview"
                ? "border-primary-500 text-primary-600"
                : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
            } whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium`}
          >
            Overview
          </button>
          {!fieldManager && (
            <button
              onClick={() => setActiveTab("interactions")}
              className={`${
                activeTab === "interactions"
                  ? "border-primary-500 text-primary-600"
                  : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
              } whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium`}
            >
              Interactions
            </button>
          )}
          {!fieldManager && (
            <button
              onClick={() => setActiveTab("tasks")}
              className={`${
                activeTab === "tasks"
                  ? "border-primary-500 text-primary-600"
                  : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
              } whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium`}
            >
              Support Tasks
            </button>
          )}
          <button
            onClick={() => setActiveTab("notes")}
            className={`${
              activeTab === "notes"
                ? "border-primary-500 text-primary-600"
                : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
            } whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium`}
          >
            Notes
          </button>
          <button
            onClick={() => setActiveTab("activity")}
            className={`${
              activeTab === "activity"
                ? "border-primary-500 text-primary-600"
                : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
            } whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium`}
          >
            Activity Log
          </button>
          <button
            onClick={() => setActiveTab("emails")}
            className={`${
              activeTab === "emails"
                ? "border-primary-500 text-primary-600"
                : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
            } whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium`}
          >
            Email, SMS & Chat History
          </button>
          {smsConversationsAvailable && (
            <button
              onClick={() => setActiveTab("sms")}
              className={`${
                activeTab === "sms"
                  ? "border-primary-500 text-primary-600"
                  : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
              } whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium`}
            >
              SMS messages
            </button>
          )}
          <button
            onClick={() => setActiveTab("vouchers")}
            className={`${
              activeTab === "vouchers"
                ? "border-primary-500 text-primary-600"
                : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
            } whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium`}
          >
            Vouchers
          </button>
          <button
            onClick={() => setActiveTab("membership")}
            className={`${
              activeTab === "membership"
                ? "border-primary-500 text-primary-600"
                : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
            } whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium`}
          >
            Membership
          </button>
          {!fieldManager && (
            <button
              onClick={() => setActiveTab("segments")}
              className={`${
                activeTab === "segments"
                  ? "border-primary-500 text-primary-600"
                  : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
              } whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium`}
            >
              Segments
            </button>
          )}
          <button
            onClick={() => setActiveTab("education")}
            className={`${
              activeTab === "education"
                ? "border-primary-500 text-primary-600"
                : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
            } whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium`}
          >
            Education
          </button>
          <button
            onClick={() => setActiveTab("events")}
            className={`${
              activeTab === "events"
                ? "border-primary-500 text-primary-600"
                : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
            } whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium`}
          >
            Events
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white shadow rounded-lg p-6">
        {activeTab === "overview" && (
          <div className="space-y-6">
            {/* Personal */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Personal
              </h2>
              <ContactPersonalSection
                contact={contact}
                organization={organization}
              />
            </div>

            {/* Communication Preferences — opt-out / suppression status + dates, so support
                and marketing can see at the contact level which channels are reachable. */}
            <div className="border-t border-gray-200 pt-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Communication Preferences
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Email
                  </label>
                  {contact.email_opt_in === false ||
                  contact.email_opt_out_at ? (
                    <p className="mt-1 text-sm font-medium text-red-700">
                      Opted out
                      {contact.email_opt_out_at
                        ? ` on ${formatDate(contact.email_opt_out_at)}`
                        : ""}
                    </p>
                  ) : (
                    <p className="mt-1 text-sm text-green-700">Subscribed</p>
                  )}
                  {contact.is_email_suppressed && (
                    <p className="mt-1 text-sm text-amber-700">
                      Suppressed
                      {contact.email_suppression_updated_at
                        ? ` on ${formatDate(contact.email_suppression_updated_at)}`
                        : ""}
                      {contact.email_suppression_reason
                        ? ` — ${contact.email_suppression_reason}`
                        : ""}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    SMS
                  </label>
                  <ContactSmsStatus
                    data={smsSingle.data}
                    isLoading={smsSingle.isLoading}
                    isError={smsSingle.isError}
                  />
                  <SmsConsentEditor
                    key={contact.id}
                    contactId={contact.id}
                    phone={contact.phone_number}
                    status={smsConsent.data}
                    singleStatus={smsSingle.data}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Phone (calls)
                  </label>
                  {contact.do_not_call ? (
                    <p className="mt-1 text-sm font-medium text-red-700">
                      Do not call
                      {contact.do_not_call_at
                        ? ` since ${formatDate(contact.do_not_call_at)}`
                        : ""}
                    </p>
                  ) : (
                    <p className="mt-1 text-sm text-green-700">OK to call</p>
                  )}
                </div>
              </div>
            </div>

            {/* Account */}
            {(subscriptions.length > 0 || householdMembers.length > 0) && (
              <div className="border-t border-gray-200 pt-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Account
                </h2>
                <ContactAccountSection
                  subscriptions={subscriptions}
                  householdMembers={householdMembers}
                  currentContactId={contact.id}
                />
              </div>
            )}

            {/* Custom Attributes */}
            <ContactCustomAttributesSection
              contactId={contact.id}
              contact={contact}
            />

            {/* Redemption Totals */}
            {contact.calendar_year_redemption_total ||
            contact.lifetime_redemption_total ? (
              <div className="border-t border-gray-200 pt-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">
                  Redemption Totals
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Calendar Year Redemptions
                    </label>
                    <p className="mt-1 text-sm text-gray-900 font-medium">
                      $
                      {(contact.calendar_year_redemption_total || 0).toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Lifetime Redemptions
                    </label>
                    <p className="mt-1 text-sm text-gray-900 font-medium">
                      ${(contact.lifetime_redemption_total || 0).toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>
            ) : null}

            {displayNotes && (
              <div className="border-t border-gray-200 pt-4">
                <label className="block text-sm font-medium text-gray-700">
                  Notes
                </label>
                <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">
                  {displayNotes}
                </p>
              </div>
            )}
          </div>
        )}

        {activeTab === "interactions" && (
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Interaction History
            </h2>
            {interactionsData?.data && interactionsData.data.length > 0 ? (
              <div className="space-y-4" data-testid="contact-interactions">
                {interactionsData.data.map((interaction) => (
                  <div
                    key={interaction.id}
                    className="border-l-4 border-primary-500 bg-gray-50 p-4"
                    data-testid="contact-interaction-row"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-900">
                        {interaction.title}
                      </span>
                      <span className="text-sm text-gray-500">
                        {formatDate(interaction.interaction_date)}
                      </span>
                    </div>
                    {(interaction.call_plan || interaction.results) && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-2 text-sm">
                        <div>
                          <span className="font-medium text-gray-500">
                            Call Plan:{" "}
                          </span>
                          {interaction.call_plan || "—"}
                        </div>
                        <div>
                          <span className="font-medium text-gray-500">
                            Results:{" "}
                          </span>
                          {interaction.results || "—"}
                        </div>
                      </div>
                    )}
                    {interaction.description && (
                      <p className="text-sm text-gray-700">
                        <span className="font-medium text-gray-500">
                          Notes:{" "}
                        </span>
                        {interaction.description}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">No interactions recorded</p>
            )}
          </div>
        )}

        {activeTab === "tasks" && (
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Support Tasks
            </h2>
            {isLoadingTasks ? (
              <LoadingSpinner size="md" text="Loading tasks..." />
            ) : contactTasks.length > 0 ? (
              <div className="space-y-4">
                {contactTasks.map((task: SupportTask) => {
                  const statusConfig = TASK_STATUS_CONFIG[task.status];
                  const snapshot = task.customer_snapshot as CustomerSnapshot;
                  return (
                    /* Clickable 2026-09-23 at the support team's request ("you cannot click on
                       the task and open it... can we open it from the contacts page to view all
                       details?"). /support-tasks?task=<id> is an EXISTING deep link whose handler
                       already fetches the task, switches to ITS status tab and selects it -- so it
                       works for a completed task without the operator guessing the status. */
                    <div
                      key={task.id}
                      role="link"
                      tabIndex={0}
                      onClick={() => navigate(`/support-tasks?task=${task.id}`)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          navigate(`/support-tasks?task=${task.id}`);
                        }
                      }}
                      title="Open this task in Support Tasks"
                      className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer hover:border-primary-300 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                              task.task_type === "birthday_message"
                                ? "bg-purple-100 text-purple-800"
                                : "bg-orange-100 text-orange-800"
                            }`}
                          >
                            {TASK_TYPE_LABELS[task.task_type] || task.task_type}
                          </span>
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusConfig.bgColor} ${statusConfig.color}`}
                          >
                            {statusConfig.label}
                          </span>
                        </div>
                        <span className="text-sm text-gray-500">
                          Due: {formatDate(task.due_date)}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        {snapshot.invoice_number && (
                          <div>
                            <span className="text-gray-500">Invoice: </span>
                            <span className="font-medium">
                              {snapshot.invoice_number}
                            </span>
                          </div>
                        )}
                        {snapshot.invoice_amount && (
                          <div>
                            <span className="text-gray-500">Amount: </span>
                            <span className="font-medium">
                              {snapshot.invoice_amount}
                            </span>
                          </div>
                        )}
                        <div>
                          <span className="text-gray-500">Assigned to: </span>
                          <span
                            className={`font-medium ${task.agent ? "" : "text-amber-700"}`}
                          >
                            {task.agent?.name ?? "Unassigned"}
                          </span>
                        </div>
                        {task.created_at && (
                          <div>
                            <span className="text-gray-500">Created: </span>
                            <span className="font-medium">
                              {formatDate(task.created_at)}
                            </span>
                          </div>
                        )}
                      </div>
                      {task.notes && (
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          <p className="text-sm text-gray-700">{task.notes}</p>
                        </div>
                      )}
                      {task.tags && task.tags.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {task.tags.map((tag) => (
                            <span
                              key={tag}
                              className="px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded-full"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-gray-500">No support tasks for this contact</p>
            )}
          </div>
        )}

        {activeTab === "notes" && <ContactNotesTab contactId={id!} />}

        {activeTab === "activity" && <ActivityLogTab contactId={id!} />}

        {activeTab === "emails" && <EmailHistoryTab contactId={id!} />}

        {activeTab === "events" && <EventCheckinsTab contactId={id!} />}

        {activeTab === "sms" && smsConversationsAvailable && (
          <div className="space-y-2 text-sm">
            <p className="text-gray-700">
              Conversations are listed by phone number in the SMS inbox.
            </p>
            <Link
              to="/messages"
              className="font-medium text-primary-600 hover:text-primary-800"
            >
              Open SMS inbox
            </Link>
          </div>
        )}

        {activeTab === "vouchers" && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Vouchers</h2>
              <Button
                variant="secondary"
                onClick={() => setIsIssueRewardOpen(true)}
              >
                Issue Reward
              </Button>
            </div>
            {isLoadingVouchers ? (
              <LoadingSpinner size="md" text="Loading vouchers…" />
            ) : contactVouchers.length === 0 ? (
              <p className="text-gray-500">No vouchers for this contact</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Code
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Name
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Amount
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Event
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Issued
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Redeemed
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {contactVouchers.map((v) => (
                      <tr key={v.id}>
                        <td className="px-4 py-3 text-sm font-mono text-gray-900">
                          {v.voucher_code}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {v.voucher_name || "—"}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {(() => {
                            // Face value; fall back to what was actually applied if a voucher
                            // carries no discount_value.
                            const amount =
                              v.discount_value ?? v.applied_amount ?? null;
                            return amount !== null && amount !== undefined
                              ? `$${Number(amount).toFixed(2)}`
                              : "—";
                          })()}
                        </td>
                        <td className="px-4 py-3">
                          {/* effective_status, NOT the raw `status` column: a redeemed voucher
                              can still carry status='active' (e.g. PROD 6DXJOA0Q), and expiry is
                              date-derived. Same authority as the Redemptions page + CSV. */}
                          <span
                            className={`px-2 py-0.5 text-xs font-medium rounded-full ${VOUCHER_STATUS_CLASSES[v.effective_status] ?? "bg-blue-100 text-blue-800"}`}
                          >
                            {v.effective_status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {/* Effective event: direct event_id OR the event behind token_id. */}
                          {v.effective_event_title || "—"}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {v.issued_at ? formatDate(v.issued_at) : "—"}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {v.redeemed_at ? formatDate(v.redeemed_at) : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {contactVouchersResult?.truncated && (
                  <p className="mt-3 text-sm text-amber-700">
                    Showing the {contactVouchersResult.limit} most recent
                    vouchers only — this contact has more. Use the Redemptions
                    page to see the full history.
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === "membership" && (
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Membership History
            </h2>
            {membershipHistory && membershipHistory.length > 0 ? (
              <div className="space-y-4">
                {membershipHistory.map((record: any, index: number) => (
                  <div
                    key={index}
                    className="border border-gray-200 rounded-lg p-4"
                  >
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Start Date
                        </label>
                        <p className="mt-1 text-sm text-gray-900">
                          {formatDate(record.start_date)}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          End Date
                        </label>
                        <p className="mt-1 text-sm text-gray-900">
                          {record.end_date
                            ? formatDate(record.end_date)
                            : "Active"}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">No membership history available</p>
            )}
          </div>
        )}

        {activeTab === "segments" && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">
                Segment Memberships
              </h2>
              <div className="relative">
                <button
                  onClick={() =>
                    setShowAddToSegmentDropdown(!showAddToSegmentDropdown)
                  }
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 flex items-center gap-2"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  Add to Segment
                </button>
                {showAddToSegmentDropdown && (
                  <div className="absolute right-0 mt-2 w-64 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-50">
                    <div className="py-1">
                      {availableSegments.length === 0 ? (
                        <div className="px-4 py-3 text-sm text-gray-500">
                          {allStaticSegments.length === 0
                            ? "No static segments available"
                            : "Contact is in all available segments"}
                        </div>
                      ) : (
                        <>
                          <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase border-b">
                            Select a segment
                          </div>
                          {availableSegments.map((segment: Segment) => (
                            <button
                              key={segment.id}
                              onClick={() =>
                                addToSegmentMutation.mutate(segment.id)
                              }
                              disabled={addToSegmentMutation.isPending}
                              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                            >
                              <div className="font-medium">{segment.name}</div>
                              <div className="text-xs text-gray-500">
                                {segment.contact_count || 0} contacts
                              </div>
                            </button>
                          ))}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {isLoadingSegments ? (
              <LoadingSpinner size="md" text="Loading segments..." />
            ) : contactSegments.length > 0 ? (
              <div className="space-y-3">
                {contactSegments.map((segment: Segment) => (
                  <div
                    key={segment.id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg group"
                  >
                    <div>
                      <div className="font-medium text-gray-900">
                        {segment.name}
                      </div>
                      {segment.description && (
                        <div className="text-sm text-gray-500">
                          {segment.description}
                        </div>
                      )}
                      <div className="text-xs text-gray-400 mt-1">
                        {segment.contact_count || 0} contacts
                      </div>
                    </div>
                    <button
                      onClick={() =>
                        removeFromSegmentMutation.mutate(segment.id)
                      }
                      disabled={removeFromSegmentMutation.isPending}
                      className="text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                      title="Remove from segment"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">
                This contact is not in any segments
              </p>
            )}
          </div>
        )}

        {activeTab === "education" && (
          <div>
            {/* Education Marketplace participation flag (set by /v1/contacts/sync).
                Future: this contact's marketplace class sign-ups list goes here. */}
            <div className="mb-6 flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700">
                Marketplace Member:
              </span>
              <span
                className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  contact.is_marketplace_member
                    ? "bg-green-100 text-green-800"
                    : "bg-gray-100 text-gray-600"
                }`}
              >
                {contact.is_marketplace_member ? "Yes" : "No"}
              </span>
            </div>

            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Training Completions
            </h2>
            {isLoadingCompletions ? (
              <LoadingSpinner size="md" text="Loading completions..." />
            ) : classCompletions.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Class Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Class ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Start Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Completion Date
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {classCompletions.map((completion: ClassCompletion) => (
                      <tr key={completion.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {completion.class_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {completion.class_id}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(completion.class_start_date)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(completion.completion_date)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-500">No training completions recorded</p>
            )}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          if (searchParams.has("edit")) {
            searchParams.delete("edit");
            setSearchParams(searchParams, { replace: true });
          }
        }}
        title="Edit Contact"
      >
        <ContactForm
          contact={contact}
          onSuccess={() => {
            // Contact row + both SMS status queries (a phone edit changes them).
            void refreshContactAfterEdit(queryClient, id!);
            setIsEditModalOpen(false);
            if (searchParams.has("edit")) {
              searchParams.delete("edit");
              setSearchParams(searchParams, { replace: true });
            }
          }}
          onCancel={() => {
            setIsEditModalOpen(false);
            if (searchParams.has("edit")) {
              searchParams.delete("edit");
              setSearchParams(searchParams, { replace: true });
            }
          }}
        />
      </Modal>

      {/* SMS Modal */}
      <SendSMSModal
        agentName={user?.full_name}
        isOpen={isSMSModalOpen}
        onClose={() => setIsSMSModalOpen(false)}
        contact={contact}
      />

      {/* Email Modal */}
      <SendEmailModal
        isOpen={isEmailModalOpen}
        onClose={() => setIsEmailModalOpen(false)}
        contact={contact}
      />

      {/* Start a Sequence Modal */}
      <StartSequenceModal
        isOpen={isStartSequenceOpen}
        onClose={() => setIsStartSequenceOpen(false)}
        contact={contact}
      />

      {/* Issue Reward Modal */}
      <IssueRewardModal
        isOpen={isIssueRewardOpen}
        onClose={() => setIsIssueRewardOpen(false)}
        initialFirstName={contact.first_name}
        initialLastName={contact.last_name}
        initialEmail={contact.email ?? ""}
        initialState={contact.state ?? contact.billing_state ?? ""}
        onSuccessDismiss={() => {
          setIsIssueRewardOpen(false);
          queryClient.invalidateQueries({
            queryKey: ["contact-vouchers", id, contact.email],
            exact: true,
          });
        }}
      />
    </div>
  );
};

export default ContactDetail;
